import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Pencil, X, ImagePlus, CircleUserRound } from 'lucide-react';
import { api, type Asset } from '../lib/api';
import { cn } from '../lib/cn';
import { AssetUploader } from './AssetUploader';
import { CoverImage } from './CoverImage';
import { FocalPointPicker } from './FocalPointPicker';

type BrandingAssets = { banner: Asset | null; logo: Asset | null };

function pickLatest(assets: Asset[]): BrandingAssets {
  const sorted = [...assets].sort((a, b) => b.createdAt - a.createdAt);
  return {
    banner: sorted.find(a => a.assetType === 'banner') ?? null,
    logo: sorted.find(a => a.assetType === 'logo') ?? null,
  };
}

function monogram(name: string | undefined | null): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function ClanBrandingHero({
  clanName,
  description,
  editable = false,
  className,
}: {
  clanName: string;
  description?: string;
  editable?: boolean;
  className?: string;
}) {
  const qc = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['clanAssets', clanName],
    queryFn: () => api.listAssets('clan', clanName),
  });

  const { banner, logo } = useMemo<BrandingAssets>(
    () => pickLatest(data?.assets ?? []),
    [data]
  );

  // Local optimistic object-position overrides while dragging — the picker is
  // fully controlled, so the frame needs an immediate value on every pointer
  // move rather than waiting on a network round-trip. Reset whenever the
  // underlying asset itself changes (a fresh upload) so a stale drag position
  // from the OLD image can never leak onto a new one.
  const [bannerPosOverride, setBannerPosOverride] = useState<string | undefined>();
  const [logoPosOverride, setLogoPosOverride] = useState<string | undefined>();
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => setBannerPosOverride(undefined), [banner?.assetId]);
  useEffect(() => setLogoPosOverride(undefined), [logo?.assetId]);

  const refresh = () => qc.invalidateQueries({ queryKey: ['clanAssets', clanName] });

  // Debounced persistence — FocalPointPicker's onChange fires on every
  // pointer move while dragging; buffering ~400ms after the last move keeps
  // this from hammering PATCH /assets/:id on every pixel of movement.
  function saveObjectPosition(assetId: string, pos: string) {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.updateAsset(assetId, { objectPosition: pos }).then(refresh).catch(() => {});
    }, 400);
  }

  const bannerPos = bannerPosOverride ?? banner?.objectPosition;
  const logoPos = logoPosOverride ?? logo?.objectPosition;

  return (
    <div className={cn('relative', className)}>
      {/* Hero card */}
      <div className="relative overflow-hidden rounded-xl shadow-elev2">
        {/* Banner layer */}
        <div className="relative aspect-[16/6] w-full bg-gradient-to-br from-forge-900/50 via-iron-900 to-iron-950">
          {banner ? (
            <CoverImage
              src={banner.url}
              alt={`${clanName} banner`}
              objectPosition={bannerPos}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex flex-col items-center gap-2 opacity-40">
                <ImagePlus className="h-6 w-6 text-forge-400" strokeWidth={2} />
                <span className="font-mono text-[10px] uppercase tracking-wider text-forge-400">
                  No banner yet
                </span>
              </div>
            </div>
          )}

          {/* Gradient scrim to keep text readable regardless of banner contents */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-iron-950/85 via-iron-950/10 to-transparent" />

          {/* Edit affordance */}
          {editable && (
            <button
              onClick={() => setDrawerOpen(true)}
              className="absolute right-3 top-3 flex items-center gap-1.5 border border-forge-500/60 bg-iron-950 px-2.5 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-forge-200 backdrop-blur-md transition-all hover:border-forge-400 hover:bg-iron-950 hover:text-white"
            >
              <Pencil className="h-3 w-3" strokeWidth={2.5} />
              {banner ? 'Change banner' : 'Add banner'}
            </button>
          )}
        </div>

        {/* Bottom row: logo + name + description */}
        <div className="relative flex items-end gap-4 px-5 pb-4 pt-0 sm:px-6">
          {/* Logo */}
          <div className="relative -mt-10 flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-iron-900 ring-2 ring-iron-950 sm:-mt-12 sm:h-24 sm:w-24">
            {logo ? (
              <CoverImage src={logo.url} alt={`${clanName} logo`} objectPosition={logoPos} className="h-full w-full object-cover" />
            ) : (
              <span className="font-display text-2xl font-black uppercase tracking-tight text-forge-400 sm:text-3xl">
                {monogram(clanName)}
              </span>
            )}
            {editable && (
              <button
                onClick={() => setDrawerOpen(true)}
                className="absolute inset-0 flex items-center justify-center bg-iron-950 opacity-0 transition-opacity hover:opacity-100"
                title={logo ? 'Change logo' : 'Add logo'}
              >
                <CircleUserRound className="h-6 w-6 text-forge-300" strokeWidth={2} />
              </button>
            )}
          </div>

          {/* Name + description */}
          <div className="min-w-0 flex-1 pt-3">
            <h1 className="truncate font-display text-2xl font-black uppercase tracking-tight text-white sm:text-3xl">
              {clanName}
            </h1>
            {description && (
              <p className="mt-0.5 truncate text-xs text-iron-400 sm:text-sm">{description}</p>
            )}
          </div>
        </div>
      </div>

      {/* Edit drawer */}
      {editable && drawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          {/* Side drawer */}
          <div className="fixed inset-y-0 right-0 z-[80] flex w-full max-w-md flex-col border-l border-forge-900/60 bg-iron-950 shadow-forged">
            <div className="flex items-center justify-between gap-3 border-b border-forge-900/40 px-5 py-4">
              <div>
                <p className="font-display text-[10px] font-black uppercase tracking-[0.22em] text-forge-400">
                  Clan branding
                </p>
                <p className="mt-0.5 font-display text-base font-black uppercase tracking-tight text-white">
                  {clanName}
                </p>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="flex h-8 w-8 items-center justify-center border border-forge-800/60 text-iron-500 transition-colors hover:border-forge-500 hover:text-forge-300"
                aria-label="Close"
              >
                <X className="h-3.5 w-3.5" strokeWidth={2.5} />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto px-5 py-5">
              <section>
                <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Banner
                </p>
                <p className="mb-3 text-xs text-iron-500">
                  Wide image, shown across the top. Recommended 1600×600, under 5MB.
                </p>
                {banner && (
                  <div className="mb-3 overflow-hidden border border-iron-700">
                    <CoverImage src={banner.url} alt="Current banner" objectPosition={bannerPos} className="h-32 w-full object-cover" />
                    <p className="border-t border-iron-800 bg-iron-950 px-2 py-1 font-mono text-[9px] uppercase tracking-wider text-iron-500">
                      Current
                    </p>
                  </div>
                )}
                {banner && (
                  <div className="mb-3">
                    <FocalPointPicker
                      src={banner.url}
                      aspect={16 / 6}
                      value={bannerPos}
                      onChange={pos => {
                        setBannerPosOverride(pos);
                        saveObjectPosition(banner.assetId, pos);
                      }}
                      label="Focal point"
                    />
                  </div>
                )}
                <AssetUploader
                  ownerType="clan"
                  ownerId={clanName}
                  assetType="banner"
                  maxSizeMb={5}
                  autoUpload
                  onUploaded={() => refresh()}
                />
              </section>

              <div className="h-px bg-forge-900/30" />

              <section>
                <p className="mb-2 font-display text-[10px] font-black uppercase tracking-[0.22em] text-iron-400">
                  Logo
                </p>
                <p className="mb-3 text-xs text-iron-500">
                  Square. Shown on the hero, match cards, and standings. Recommended 512×512.
                </p>
                {logo && (
                  <div className="mb-3 flex items-center gap-3 border border-iron-700 p-2">
                    <CoverImage src={logo.url} alt="Current logo" objectPosition={logoPos} className="h-14 w-14 object-cover" />
                    <p className="font-mono text-[9px] uppercase tracking-wider text-iron-500">
                      Current logo
                    </p>
                  </div>
                )}
                {logo && (
                  <div className="mb-3">
                    <FocalPointPicker
                      src={logo.url}
                      aspect={1}
                      value={logoPos}
                      onChange={pos => {
                        setLogoPosOverride(pos);
                        saveObjectPosition(logo.assetId, pos);
                      }}
                      label="Focal point"
                    />
                  </div>
                )}
                <AssetUploader
                  ownerType="clan"
                  ownerId={clanName}
                  assetType="logo"
                  maxSizeMb={5}
                  autoUpload
                  onUploaded={() => refresh()}
                />
              </section>
            </div>

            <div className="border-t border-forge-900/40 px-5 py-3">
              <p className="font-mono text-[9px] uppercase tracking-wider text-iron-500">
                Pick an image — it saves automatically. You'll see "✓ Saved" and the preview update.
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
